/**
 * Centralised checkout validation logic. Mirrors the rules applied by the
 * legacy shop (see /app/ihtp-crm-source/ihtp-crm/shop/src/helpers/UtilsCheckout.tsx
 * and components/Checkout/Total.tsx):
 *
 *   1) If ProdutosTodosGratuitos !== true → billing address required
 *   2) If TemProdutosFisicos === 1 → shipping method required
 *   3) If TemProdutosFisicos === 1 && TipoTransporteExterno === 'S' → shipping address required
 *   4) If Total > 0 → payment method required
 *   5) If paymentType === 'MW' (MBWay) → phone number required
 *   6) Shipping method ValorMinimo must be satisfied by subtotal
 *   7) Payment method ValorMinimo must be satisfied by total
 *
 * Returns { ok: true } when validation passes, or { ok: false, message } with
 * the first problem found (Portuguese copy — matches legacy Toast messages).
 */

import { isCompanyNif } from "./checkout/nif";

export function validateCheckout({
  verifyData,
  billingAddressId,
  shippingAddressId,
  deliveryMethod,
  deliveryMethods = [],
  paymentMethod,
  paymentMethods = [],
  mbwayPhone,
  cartEmpty,
  // NIF of the selected billing address — required for Cofidis Pay validation.
  // Accept raw string; callers should pass the address's `Nif` field.
  billingNif,
  // When true, skip payment-method related checks (used in the pre-payment
  // step where the user hasn't picked one yet — the next screen handles it).
  skipPaymentCheck = false,
  // Centralized override do shipping requirement — se o frontend determinar
  // (via /checkout/cart-flags) que o carrinho não tem produtos LI/PM, força
  // `temProdutosFisicos=false` aqui mesmo que o legacy diga o contrário.
  // Isto permite que CDs/DVDs/etc também sigam para "Levantamento em evento".
  requiresShippingOverride,
} = {}) {
  if (cartEmpty) return fail("O carrinho está vazio.", "cart");

  const v = verifyData || {};
  const temProdutosFisicos = (typeof requiresShippingOverride === "boolean")
    ? requiresShippingOverride
    : Number(v.TemProdutosFisicos ?? 0) === 1;
  const todosGratuitos = v.ProdutosTodosGratuitos === true;
  const tipoTransporteExterno = v.TipoTransporteExterno; // 'S' | 'N' | null
  const total = Number(v.Total ?? 0);
  const subtotal = Number(v.Subtotal ?? v.SubTotal ?? total);

  // 1) Billing address: whenever anything is paid
  if (!todosGratuitos && !billingAddressId) {
    return fail("Seleccione a morada de faturação.", "billing");
  }

  // 2) Shipping method required if physical items exist
  if (temProdutosFisicos && !deliveryMethod) {
    return fail("Seleccione a forma de envio.", "delivery");
  }

  // 6) Shipping method minimum
  if (temProdutosFisicos && deliveryMethod) {
    const dm = deliveryMethods.find((d) =>
      String(d.ID_TipoTransporte ?? d.Id) === String(deliveryMethod));
    const min = Number(dm?.ValorMinimo || 0);
    if (min > 0 && subtotal < min) {
      return fail(`A forma de envio "${dm.Descricao}" exige uma encomenda mínima de ${fmt(min)}.`, "delivery");
    }
  }

  // 3) Shipping address required only when shipment is external
  // Fallback: if legacy didn't fill tipoTransporteExterno yet, derive it from
  // the picked delivery method so we still catch the error before submit.
  const externalDelivery = tipoTransporteExterno === "S"
    || (deliveryMethod && deliveryMethods.find((d) =>
      String(d.ID_TipoTransporte ?? d.Id) === String(deliveryMethod))?.Externo === "S");
  if (temProdutosFisicos && externalDelivery && !shippingAddressId) {
    return fail("Seleccione a morada de envio.", "shipping");
  }

  // 4) Payment method required when there's anything to pay
  if (!skipPaymentCheck && total > 0 && !paymentMethod) {
    return fail("Seleccione o método de pagamento.", "payment");
  }

  // 7) Payment method minimum
  if (!skipPaymentCheck && paymentMethod) {
    const pm = paymentMethods.find((p) =>
      String(p.ID_TipoPagamento ?? p.Id) === String(paymentMethod));
    const min = Number(pm?.ValorMinimo || 0);
    if (min > 0 && total < min) {
      return fail(`O método "${pm.Descricao}" exige um total mínimo de ${fmt(min)}.`, "payment");
    }
  }

  // 5) MBWay requires a phone number
  if (!skipPaymentCheck && paymentMethod === "MW" && !(mbwayPhone || "").trim()) {
    return fail("Introduza o número de telemóvel para MBWay.", "mbwayPhone");
  }

  // 8) Cofidis Pay (CP) rules — espelham o shop legacy:
  //     Total ∈ [60 €, 2500 €] inclusive + NIF particular (não inicia por 5–9).
  if (!skipPaymentCheck && paymentMethod === "CP") {
    if (total < 60 || total > 2500) {
      return fail("Cofidis Pay: o valor total tem de estar entre 60 € e 2500 €.", "payment");
    }
    if (isCompanyNif(billingNif)) {
      return fail("Cofidis Pay não está disponível para NIFs de empresa. Selecione uma morada de facturação com NIF particular.", "billing");
    }
  }

  return { ok: true };
}

function fail(message, field) { return { ok: false, message, field }; }
function fmt(v) {
  return new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v);
}
